// ============ BaZi panel ============
// Another reading of the same birth moment, sitting beside the Human Design
// chart rather than replacing it.
//
//   BaZi (八字) — the Four Pillars. Engine ported from hd-core: the year turns
//                 at Lichun and each month at a jie, both from the real Sun,
//                 so this is not a lookup table. Verified against the project's
//                 documented test chart (1994-02-20 21:34 Bangkok → 丙寅 month,
//                 male luck pillars 丁卯 戊辰 己巳 forward from age ~4.5).
//
// The third reading, I Ching, outgrew this file and lives in `ichingpanel.tsx`.

import React, { useEffect, useMemo, useState } from 'react';
import { ELEMENTS } from '../lib/paje/engine.js';
import { BaziExtras } from './baziextras';
import { useT, useLang } from '../lib/i18n';

const ELEMENT_COLOR = ['#4ade80', '#ff5c4d', '#f0c75a', '#cbd5e1', '#38bdf8']; // wood fire earth metal water

const SEASONAL_TH: Record<string, string> = {
  wang: 'วั่ง (旺) — แข็งแรงที่สุดตามฤดู',
  xiang: 'เซี่ยง (相) — ได้รับการเกื้อหนุน',
  xiu: 'ซิว (休) — พักตัว',
  qiu: 'ชิว (囚) — ถูกกักขัง',
  si: 'ซื่อ (死) — อ่อนแรงที่สุด',
};

const ELEMENT_C = ['#4ade80', '#ff5c4d', '#f0c75a', '#cbd5e1', '#38bdf8']; // wood fire earth metal water
const TONE_C: Record<string, string> = { good: 'var(--up)', warn: 'var(--gold)', bad: 'var(--down)' };

/**
 * The BaZi tab, rendered from the PA JE stack — its engine, its analysis layer
 * and its guidance layer, vendored whole from D:\PA JE. That project states the
 * same rule this desk works to, and it is worth restating: separate what is
 * CALCULATED from what is INTERPRETED, and when schools disagree show both
 * answers rather than quietly picking one. Hence the 23:00 box, the `why` on
 * every guidance line, and the `basis` printed under every modelled number.
 *
 * The stack carries ~190 KB of reference tables, so it loads only when this tab
 * is opened.
 */
export function BaziPanel({ birthUTC, tzHours }: { birthUTC: Date; tzHours: number }) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [mod, setMod] = useState<any>(null);
  const [openDecade, setOpenDecade] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    // data.js is already pulled in by analysis.js, so naming it here costs
    // nothing extra in the chunk and gives the sexagenary table its source.
    Promise.all([import('../lib/paje/analysis.js'), import('../lib/paje/guidance.js'), import('../lib/paje/data.js')])
      .then(([an, gu, data]) => { if (alive) setMod({ an, gu, data }); });
    return () => { alive = false; };
  }, []);

  const a = useMemo(
    () => (mod ? mod.an.analyze({ birthUTC, tzOffsetMinutes: tzHours * 60, sex }) : null),
    [mod, birthUTC, tzHours, sex]);
  const g = useMemo(() => (a ? mod.gu.buildGuidance(a) : null), [a, mod]);

  if (!a || !g) return <p className="dim">{t('กำลังโหลดตำราปาจื่อ…', 'Loading the BaZi tables…')}</p>;

  const st = a.strength;
  const nowAge = (Date.now() - birthUTC.getTime()) / (365.2425 * 864e5);

  const Why = ({ why }: { why?: string }) => (why ? <em className="mm-why">{why}</em> : null);
  const Block = ({ head, items }: { head: string; items: any[] }) => (
    <div className="mm-read">
      <h4 className="mm-read-h">{head}</h4>
      {items.map((it: any, i: number) => (
        <p className="mm-read-p" key={i}>{it.text}<Why why={it.why}></Why></p>
      ))}
    </div>
  );

  return (
    <div className="mm-bazi">
      {/* Day Master — every other character is read as helping or draining it. */}
      <div className="mm-bazi-dm" style={{ borderColor: ELEMENT_C[st.dmElement] + '66' }}>
        <span className="mono dim">{t('เจ้าชะตา (日主 Day Master)', 'Day Master (日主)')}</span>
        <b style={{ color: ELEMENT_C[st.dmElement] }}>
          {a.dayMaster.info.chinese} {en ? a.dayMaster.info.en : a.dayMaster.info.th}
        </b>
        {a.dayMaster.extra?.imageryTh && <span className="dim">{a.dayMaster.extra.imageryTh}</span>}
        <span className="dim">
          {st.verdictZh} ({st.verdict}) · {st.percent.toFixed(1)}%
          {a.xun?.chinese ? ` · ${t('รอบ', 'xun')} ${a.xun.chinese}` : ''}
          {a.voidBranches?.length ? ` · ${t('ว่าง 空亡', 'void 空亡')} ${a.voidBranches.map((v: any) => v.chinese || v).join(' ')}` : ''}
        </span>
      </div>

      {/* The one point where the two mainstream schools genuinely disagree. */}
      {a.lateZi && a.lateZi.differs && (
        <div className="mm-bazi-school">
          <b className="mono">{t('เกิดช่วง 23:00 — สองสำนักให้คนละคำตอบ', 'Born in the 23:00 hour — the schools disagree')}</b>
          <span>{t('สำนักเที่ยงคืน (ที่แสดงอยู่)', 'Midnight school (shown)')}: {a.lateZi.thisSchool.day} / {a.lateZi.thisSchool.hour} — {t('เจ้าชะตา', 'DM')} {a.lateZi.thisSchool.dayMaster}</span>
          <span>{t('สำนัก 23:00 ขึ้นวันใหม่', '23:00 school')}: {a.lateZi.otherSchool.day} / {a.lateZi.otherSchool.hour} — {t('เจ้าชะตา', 'DM')} {a.lateZi.otherSchool.dayMaster}</span>
          <span className="dim">{t('เจ้าชะตาคนละตัวคือดวงคนละใบ — แสดงทั้งสองไว้ ไม่เลือกข้างแทนคุณ', 'A different Day Master is a different chart — both are shown rather than one chosen for you.')}</span>
        </div>
      )}

      {/* Four pillars, every position annotated. */}
      <div className="mm-bazi-grid">
        {a.pillars.map((p: any) => (
          <div className={`mm-bazi-col ${p.isVoid ? 'void' : ''}`} key={p.key}>
            <span className="mm-bazi-lbl mono">{en ? p.key : p.th}<em className="dim">{p.palace?.th || p.zh}</em></span>
            <b className="mm-bazi-han" style={{ color: ELEMENT_C[p.stem.element] }} title={p.stem.info.pinyin}>{p.stem.info.chinese}</b>
            <span className="mm-bazi-god mono">{p.stemTenGod ? (en ? p.stemTenGod.en : p.stemTenGod.th) : t('ตัวเรา', 'self')}</span>
            <b className="mm-bazi-han" style={{ color: ELEMENT_C[p.branch.element] }} title={p.branch.info.pinyin}>{p.branch.info.chinese}</b>
            <small className="dim">{en ? p.branch.info.animalEn : p.branch.info.animalTh}</small>
            <small className="dim">{p.stage?.chinese} {en ? p.stage?.en : p.stage?.th}</small>
            {/* Hidden stems carry most of a branch's meaning. */}
            <div className="mm-bazi-hidden">
              {p.hidden.map((h: any, i: number) => (
                <span key={i} title={`${h.roleChinese} · ${h.weight}%`}>
                  <b style={{ color: ELEMENT_C[h.element] }}>{h.stemChinese}</b>
                  <em>{en ? h.tenGod.en : h.tenGod.th}</em>
                </span>
              ))}
            </div>
            {p.nayin && <small className="dim mm-bazi-nayin">{p.nayin.chinese} · {en ? p.nayin.en : p.nayin.th}</small>}
            {p.isVoid && <span className="mm-bazi-voidtag mono">空亡</span>}
          </div>
        ))}
      </div>

      {/* Weighted element balance — position and hidden-stem share, not a headcount. */}
      <div className="mm-bazi-el">
        {a.scores.percent.map((n: number, i: number) => (
          <div className="mm-bazi-elrow" key={i}>
            <span className="mono" style={{ color: ELEMENT_C[i] }}>{ELEMENTS[i].chinese} {en ? ELEMENTS[i].en : ELEMENTS[i].th}</span>
            <span className="mm-bazi-bar"><i style={{ width: `${n}%`, background: ELEMENT_C[i] }}></i></span>
            <b className="mono">{n.toFixed(1)}%</b>
          </div>
        ))}
      </div>

      {/* Strength with its workings, not just a verdict. */}
      <div className="mm-bazi-checks">
        {st.checks.map((c: any, i: number) => (
          <div className={`mm-bazi-check ${c.pass ? 'on' : ''}`} key={i}>
            <b className="mono">{c.zh}</b><span>{c.th}</span><em className="dim">{c.detail}</em>
          </div>
        ))}
      </div>
      <div className="mm-bazi-fav mono">
        <span className="up">用神 {t('ธาตุที่เป็นคุณ', 'favourable')}: {a.usefulGod.favorable.map((e: number) => (en ? ELEMENTS[e].en : ELEMENTS[e].th)).join(' · ')}</span>
        <span className="down">{t('ธาตุต้องห้าม', 'unfavourable')}: {a.usefulGod.unfavorable.map((e: number) => (en ? ELEMENTS[e].en : ELEMENTS[e].th)).join(' · ')}</span>
      </div>
      <p className="mm-footnote dim">{a.usefulGod.method} · {st.basis}</p>

      {/* Relations — where a chart pulls against itself. */}
      {a.relations.length > 0 && (
        <div className="mm-bazi-rels">
          <span className="mono dim">{t('ความสัมพันธ์ที่พบในดวง', 'Relations found in the chart')}</span>
          {a.relations.map((r: any, i: number) => (
            <div className="mm-bazi-rel" key={i} style={{ borderColor: (TONE_C[r.tone] || 'var(--line-soft)') + '66' }}>
              <b style={{ color: TONE_C[r.tone] }}>{r.chinese}</b>
              <span>{r.kind}</span>
              <em className="dim">{r.where}{r.note ? ` · ${r.note}` : ''}</em>
            </div>
          ))}
        </div>
      )}

      {/* 神煞 — symbolic stars, each with what it was derived from. */}
      {a.shensha.length > 0 && (
        <div className="mm-bazi-stars">
          <span className="mono dim">{t('ดาวสัญลักษณ์ (神煞)', 'Symbolic stars (神煞)')}</span>
          <div className="mm-bazi-starlist">
            {a.shensha.map((h: any, i: number) => (
              <span className={`mm-bazi-star ${h.star.polarity}`} key={i} title={h.from}>
                <b>{h.star.chinese}</b> {h.star.th} <em className="dim">{h.where.join(',')}</em>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Luck pillars, with the decade one click away. */}
      <div className="mm-bazi-luckhdr">
        <span className="mono dim">{t('เสาโชค 10 ปี (大運)', 'Luck pillars (大運)')}</span>
        <div className="mm-filterrow">
          {(['male', 'female'] as const).map(s2 => (
            <button key={s2} className={`mm-pill ${sex === s2 ? 'on' : ''}`} onClick={() => setSex(s2)}>
              {s2 === 'male' ? t('ชาย', 'Male') : t('หญิง', 'Female')}
            </button>
          ))}
        </div>
        <span className="mono dim">{a.luck.direction} · {t('เริ่มอายุ', 'from age')} {(a.luck.startAgeExact ?? a.luck.startAge).toFixed(1)}</span>
      </div>
      <div className="mm-bazi-luck">
        {g.decades.map((d: any) => {
          const active = nowAge >= d.ageFrom && nowAge <= d.ageTo;
          return (
            <div className={`mm-bazi-lp ${active ? 'on' : ''} ${openDecade === d.index ? 'sel' : ''}`}
              key={d.index} onClick={() => setOpenDecade(openDecade === d.index ? null : d.index)}>
              <b style={{ color: ELEMENT_C[d.stemElement] }}>{d.name}</b>
              <span className="mono dim">{d.ageFrom}–{d.ageTo}</span>
              <small className="dim">{d.yearFrom}</small>
              <em className="mm-bazi-lptag" style={{ color: TONE_C[d.tone] || 'var(--txt-dim)' }}>{d.tenGodTh}</em>
            </div>
          );
        })}
      </div>
      {openDecade != null && (() => {
        const d = g.decades.find((x: any) => x.index === openDecade);
        return d ? (
          <div className="mm-read">
            <h4 className="mm-read-h">{d.name} · {t('อายุ', 'age')} {d.ageFrom}–{d.ageTo} ({d.yearFrom}–{d.yearTo}) · {d.tenGodZh} {d.tenGodTh}</h4>
            <p className="mm-read-p"><b>{t('ธีมของช่วง', 'Theme')}</b>{d.theme}</p>
            <p className="mm-read-p"><b>{t('ควรทำ', 'Do')}</b>{d.doIt}</p>
            <p className="mm-read-p"><b>{t('ควรเลี่ยง', 'Avoid')}</b>{d.avoid}</p>
            <p className="mm-read-p"><b>{t('อากาศของดวง', 'Weather')}</b>{d.weather}{d.stage ? ` · ${d.stage.chinese} ${d.stage.th}` : ''}</p>
          </div>
        ) : null;
      })()}

      {/* 流年 — this year against the natal chart. */}
      <div className="mm-bazi-year">
        <b className="mono">{t('ปีไหลเวียนปีนี้ (流年)', 'This year (流年)')} {a.currentYear.name}</b>
        {a.currentYear.tenGod && <span>{en ? a.currentYear.tenGod.en : a.currentYear.tenGod.th}</span>}
        {a.currentYear.clashes.length > 0 && <span className="down">{t('ปะทะ 沖', 'clashes 沖')}: {a.currentYear.clashes.join(', ')}</span>}
        {a.currentYear.combines.length > 0 && <span className="up">{t('สมพงษ์ 合', 'combines 合')}: {a.currentYear.combines.join(', ')}</span>}
      </div>

      {/* The reading. Every line carries the chart feature it came from. */}
      <Block head={g.life.headline} items={[...(g.life.strategy || []), ...(g.life.daily || [])]}></Block>
      {g.work?.notes?.length > 0 && <Block head={g.work.headline} items={g.work.notes}></Block>}
      {g.money?.items?.length > 0 && <Block head={g.money.headline} items={g.money.items}></Block>}

      {g.phases?.length > 0 && (
        <div className="mm-read">
          <h4 className="mm-read-h">{t('สี่เสาอ่านเป็นช่วงชีวิต', 'The four pillars as a life timeline')}</h4>
          {g.phases.map((ph: any, i: number) => (
            <p className="mm-read-p" key={i}>
              <b>{ph.zh} · {ph.ages}</b>{ph.focus}
              {ph.isVoid ? <Why why={t('เสานี้ตกในช่วง 空亡', 'this pillar falls in the void')}></Why> : null}
            </p>
          ))}
        </div>
      )}

      {/* The two structures a reader would otherwise draw on paper: the five
          element cycles with their real weights on them, and the sixty-year
          cycle with this chart's own pillars marked. */}
      <BaziExtras a={a} SIXTY_JIAZI={mod.data.SIXTY_JIAZI}></BaziExtras>

      <p className="mm-footnote dim">{mod.gu.DISCLAIMER.th} — {mod.gu.DISCLAIMER.detail}</p>
    </div>
  );
}
